import React, { useState } from 'react';
import axios from '../api/Axios';

const ProviderRegisterForm = () => {
  const [formData, setFormData] = useState({
    providerType: '',
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Entity-specific fields
    companyName: '',
    representativePosition: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    
    if (!/\d/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push("Password must contain at least one special character");
    }
    
    return errors;
  };

  // Calculate password strength score
  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score++;
    return score;
  };

  // Get password strength label and color
  const getPasswordStrengthInfo = (score) => {
    switch (score) {
      case 0:
      case 1:
        return { label: 'Very Weak', color: '#dc3545', width: '20%' };
      case 2:
        return { label: 'Weak', color: '#fd7e14', width: '40%' };
      case 3:
        return { label: 'Fair', color: '#ffc107', width: '60%' };
      case 4:
        return { label: 'Good', color: '#20c997', width: '80%' };
      case 5:
        return { label: 'Strong', color: '#198754', width: '100%' };
      default:
        return { label: '', color: '', width: '0%' };
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Validate password in real-time
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    // Validation
    if (!formData.providerType) {
      setErrorMsg("Please select a provider type.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    // Validate password strength
    const passwordValidationErrors = validatePassword(formData.password);
    if (passwordValidationErrors.length > 0) {
      setErrorMsg("Please fix the password requirements:\\n" + passwordValidationErrors.join('\\n'));
      return;
    }

    // Entity-specific validation
    if (formData.providerType === 'entity' && !formData.companyName.trim()) {
      setErrorMsg("Company name is required for business entities.");
      return;
    }

    setLoading(true);

    // Compose payload for Django API
    const payload = {
      user: {
        email: formData.email,
        password: formData.password,
      },
      provider_type: formData.providerType,
      first_name: formData.firstName,
      last_name: formData.lastName,
      title: formData.title,
      ...(formData.providerType === 'entity' && {
        company_name: formData.companyName,
        representative_position: formData.representativePosition
      })
    };

    try {
      await axios.post("accounts/register/provider-step1/", payload);
      setSuccessMsg("Registration successful! Please check your email to verify your account before logging in.");
      setFormData({
        providerType: '',
        title: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        companyName: '',
        representativePosition: ''
      });
    } catch (err) {
      console.log("Full error object:", err);
      console.log("Error response data:", err.response?.data);
      
      if (err.response?.data) {
        // Convert the nested error object to a user-friendly string
        const formatErrors = (errorObj) => {
          const messages = [];
          
          const extractMessages = (obj, prefix = '') => {
            if (Array.isArray(obj)) {
              obj.forEach(msg => messages.push(prefix + msg));
            } else if (typeof obj === 'object' && obj !== null) {
              Object.entries(obj).forEach(([key, value]) => {
                const newPrefix = prefix ? `${prefix} > ${key}: ` : `${key}: `;
                extractMessages(value, newPrefix);
              });
            } else {
              messages.push(prefix + String(obj));
            }
          };
          
          extractMessages(errorObj);
          return messages.join('\\n');
        };
        
        setErrorMsg(formatErrors(err.response.data));
      } else {
        setErrorMsg("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordStrengthInfo = getPasswordStrengthInfo(passwordStrength);

  return (
    <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.17)',
        color: 'rgba(255, 255, 255)',
        padding: '2rem',
        margin: '1rem 0',
        boxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
        WebkitBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
        MozBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)'
    }}>
        <form onSubmit={handleSubmit}>
            {/* Provider Type Selection */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Provider Type *</strong>
                </label>
                <select
                    className="form-control"
                    name="providerType"
                    value={formData.providerType}
                    onChange={handleChange}
                    required
                >
                    <option value="">Select Provider Type</option>
                    <option value="individual">Individual Property Owner</option>
                    <option value="entity">Business/Organization</option>
                </select>
            </div>

            {/* Conditional fields based on provider type */}
            {formData.providerType === 'entity' && (
                <>
                    <div className="mb-3">
                        <label className="form-label text-white">
                            <strong>Company/Organization Name *</strong>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            placeholder="Enter company name"
                            required
                        />
                    </div>
                    
                    <div className="mb-3">
                        <label className="form-label text-white">Representative Position</label>
                        <input
                            type="text"
                            className="form-control"
                            name="representativePosition"
                            value={formData.representativePosition}
                            onChange={handleChange}
                            placeholder="e.g., Property Manager, CEO"
                        />
                    </div>
                </>
            )}

            {/* Representative/Personal Details */}
            {/* Title - Full width */}
            <div className="mb-3">
                <label className="form-label text-white">Title</label>
                <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Title"
                />
            </div>

            {/* Name fields - Last Name first, then Other Names */}
            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">
                            <strong>Last Name *</strong>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Last name"
                            required
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">
                            <strong>Other Names *</strong>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="Other names"
                            required
                        />
                    </div>
                </div>
            </div>

            {/* Account Details */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Email Address *</strong>
                </label>
                <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                />
            </div>

            {/* Password */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Password *</strong>
                </label>
                <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    required
                />
                
                {/* Password Requirements */}
                {formData.password && (
                    <div className="mt-2">
                        <div className="mb-2">
                            <div style={{
                                width: '100%',
                                height: '6px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '3px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: passwordStrengthInfo.width,
                                    height: '100%',
                                    backgroundColor: passwordStrengthInfo.color,
                                    transition: 'width 0.3s ease'
                                }}></div>
                            </div>
                            <small className="text-white">
                                Password Strength: <span style={{color: passwordStrengthInfo.color}}>
                                    {passwordStrengthInfo.label}
                                </span>
                            </small>
                        </div>
                        
                        {passwordErrors.length > 0 && (
                            <div>
                                <small className="text-warning">Password Requirements:</small>
                                <ul style={{fontSize: '0.75em', marginLeft: '1rem'}}>
                                    {passwordErrors.map((error, index) => (
                                        <li key={index} className="text-warning">{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Confirm Password */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Confirm Password *</strong>
                </label>
                <input
                    type="password"
                    className="form-control"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    required
                />
            </div>

            {/* Success/Error Messages */}
            {successMsg && (
                <div className="alert alert-success" role="alert">
                    {successMsg}
                </div>
            )}

            {errorMsg && (
                <div className="alert alert-danger" role="alert" style={{whiteSpace: 'pre-line'}}>
                    {errorMsg}
                </div>
            )}

            {/* Submit Button */}
            <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
            >
                {loading ? 'Registering...' : 'Register as Provider'}
            </button>

            {/* Login Link */}
            <div className="text-center mt-3">
                <small className="text-white">
                    Already have an account? <a href="/Login" className="text-light">Login here</a>
                </small>
            </div>
        </form>
    </div>
  );
};

export default ProviderRegisterForm;